import React, { useCallback, useEffect, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { SERVER_URL } from "../../ServerLink";
import {
  ASSIGNMENT_STATUS,
  KIND_LABEL,
  RATING,
  fmtDate,
  fmtMoveout,
  isVideoKey,
  placeLine,
  textOf,
} from "./turnoverFormat";
import "./public-turnover.scss";

export * from "./turnoverFormat";

// Public (token-in-the-link) endpoints. Plain fetch on purpose: the shared axios instance
// attaches the staff session and bounces to /login on a 401, which is the wrong answer for
// a vendor or approver who has no account here.
export const PUBLIC_BASE = `${SERVER_URL}/v2/pre-moveout-inspections/public`;

export function friendlyError(status, serverMessage) {
  if (status === 401) {
    return {
      title: "This link is no longer valid",
      body: "It may have expired or been replaced by a newer email. Use the most recent link you were sent, or contact the property.",
    };
  }
  if (status === 404) {
    return {
      title: "This job no longer exists",
      body: "The turnover request behind this link has been removed. If that seems wrong, contact the property.",
    };
  }
  if (!status) {
    return { title: "We could not reach the server", body: "Check your connection and try again." };
  }
  return { title: "Something went wrong", body: serverMessage || "Please try again in a moment." };
}

async function readBody(res) {
  try {
    return await res.json();
  } catch (_) {
    return {};
  }
}

/** POST to a public URL; throws an Error carrying `status` and the parsed `body` on failure. */
export async function postPublic(url, payload) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await readBody(res);
  if (!res.ok || body.success === false) {
    const err = new Error(body.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

/** Loads /public/<kind>/:id/:aid/:token and exposes { loading, error, data, reload, url }. */
export function usePublicTurnover(kind, id, aid, token) {
  const url = `${PUBLIC_BASE}/${kind}/${encodeURIComponent(id)}/${encodeURIComponent(aid)}/${encodeURIComponent(token)}`;
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetch(url);
      const body = await readBody(res);
      if (!res.ok || body.success === false || !body.data) {
        const err = new Error(body.message || "");
        err.status = res.status;
        throw err;
      }
      if (alive.current) setState({ loading: false, error: null, data: body.data });
    } catch (err) {
      if (alive.current) {
        setState({ loading: false, error: { status: err.status || 0, message: err.message || "" }, data: null });
      }
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load, url };
}

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

export const PtPage = ({ children, footer }) => (
  <div className="pt-page">
    <div className="pt-card">
      <div className="pt-brand">
        <img src="/images/rentdigicare-logo.svg" alt="Rentdigicare" />
        <span>Pre-moveout turnover</span>
      </div>
      {children}
    </div>
    {footer ? <p className="pt-foot">{footer}</p> : null}
  </div>
);

export const PtLoading = ({ text = "Loading…" }) => (
  <div className="pt-center">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading</span>
    </div>
    <p>{text}</p>
  </div>
);

export const PtError = ({ error }) => {
  const f = friendlyError(error && error.status, error && error.message);
  return (
    <div className="pt-center">
      <span className="pt-center-ic">
        <FeatherIcon icon="alert-circle" size={22} />
      </span>
      <h1>{f.title}</h1>
      <p>{f.body}</p>
    </div>
  );
};

export const StatusPill = ({ status }) => {
  const s = ASSIGNMENT_STATUS[status] || { label: status || "Unknown", tone: "muted" };
  return <span className={`pt-pill is-${s.tone}`}>{s.label}</span>;
};

export const RatingPill = ({ rating }) => {
  const r = RATING[rating];
  if (!r) return <span className="pt-pill is-muted">Not rated</span>;
  return <span className={`pt-pill is-${r.tone}`}>{r.label}</span>;
};

export const JobHeader = ({ job, eyebrow, title, children }) => {
  const company = textOf(job.company);
  return (
    <header className="pt-head">
      <p className="pt-eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p className="pt-meta">
        {placeLine(job)}
        {company ? ` · ${company}` : ""}
      </p>
      {children}
    </header>
  );
};

export const JobFacts = ({ job }) => {
  const address = textOf(job.address);
  return (
    <dl className="pt-facts">
      <div>
        <dt>Move-out</dt>
        <dd>{fmtMoveout(job.moveout_date, job.moveout_time)}</dd>
      </div>
      <div>
        <dt>Type of work</dt>
        <dd>{KIND_LABEL[job.kind] || job.kind || "—"}</dd>
      </div>
      <div>
        <dt>Unit</dt>
        <dd>{job.unit_number || "—"}</dd>
      </div>
      <div>
        <dt>Address</dt>
        <dd>{address || "—"}</dd>
      </div>
      {job.pre_inspection_date ? (
        <div>
          <dt>Pre-inspection</dt>
          <dd>{fmtDate(job.pre_inspection_date, { utc: true })}</dd>
        </div>
      ) : null}
    </dl>
  );
};

export const Section = ({ icon, title, children }) => (
  <section className="pt-section">
    <h2 className="pt-section-title">
      {icon ? <FeatherIcon icon={icon} size={16} /> : null}
      {title}
    </h2>
    {children}
  </section>
);

export const MediaGallery = ({ media }) => {
  const items = (Array.isArray(media) ? media : []).filter((m) => m && m.url);
  if (!items.length) return null;
  return (
    <div className="pt-media">
      {items.map((m, i) =>
        isVideoKey(m.key || m.url) ? (
          <div className="pt-media-video" key={m.key || i}>
            <video controls preload="metadata" playsInline>
              <source src={m.url} />
              Your browser cannot play this video.{" "}
              <a href={m.url} target="_blank" rel="noopener noreferrer">
                Download it
              </a>
              .
            </video>
          </div>
        ) : (
          <a
            className="pt-media-img"
            key={m.key || i}
            href={m.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open full size"
          >
            <img src={m.url} alt={`Attachment ${i + 1}`} loading="lazy" />
          </a>
        )
      )}
    </div>
  );
};

export const ScopeBlock = ({ scope }) => (
  <Section icon="clipboard" title="Scope of work">
    {scope ? <p className="pt-prose">{scope}</p> : <p className="pt-empty">No scope was written for this job.</p>}
  </Section>
);

/** Cleanliness, repairs (with their media) and the property's notes. */
export const EvidenceBlock = ({ job }) => {
  const c = job.cleanliness || {};
  const repairs = Array.isArray(job.repairs) ? job.repairs : [];
  return (
    <>
      <Section icon="droplet" title="Suite condition">
        <div className="pt-status-row">
          <span className="pt-label">Cleanliness</span>
          <RatingPill rating={c.rating} />
        </div>
        {c.notes ? <p className="pt-prose mt-2">{c.notes}</p> : null}
        <MediaGallery media={c.media} />
      </Section>

      <Section icon="tool" title={`Repairs${repairs.length ? ` (${repairs.length})` : ""}`}>
        {repairs.length === 0 ? (
          <p className="pt-empty">No repairs were recorded.</p>
        ) : (
          repairs.map((r, i) => (
            <div className="pt-repair" key={r._id || i}>
              <div className="pt-repair-head">
                <span className="pt-repair-n">{i + 1}.</span>
                <span className="pt-repair-cat">{r.category || "Other"}</span>
              </div>
              {r.description ? <p className="pt-prose">{r.description}</p> : null}
              <MediaGallery media={r.media} />
            </div>
          ))
        )}
      </Section>

      {job.comments || job.damages_notes ? (
        <Section icon="message-square" title="Notes from the property">
          {job.damages_notes ? (
            <p className="pt-prose">
              <strong>Damages: </strong>
              {job.damages_notes}
            </p>
          ) : null}
          {job.comments ? <p className={`pt-prose${job.damages_notes ? " mt-2" : ""}`}>{job.comments}</p> : null}
        </Section>
      ) : null}
    </>
  );
};

export const StateBox = ({ tone = "muted", icon, title, children }) => (
  <div className={`pt-state is-${tone}`}>
    {icon ? <FeatherIcon icon={icon} size={20} /> : null}
    <div>
      {title ? <h3>{title}</h3> : null}
      {children}
    </div>
  </div>
);
